/**
 * 需求来源：/partnership/home 重构为 React SSR 页（合作页 partnership_home.html 还原）。
 * 注意事项：
 *  1. 解析 ossHost / fullName（含 CSR 兜底，链路与参考页 printonDemand/home 一致）。
 *  2. 卡片列表来自后端接口 cj/partner/getAllTypePlatformList（POST，headers.language=en，返回 [err, body]，
 *     数据在 body.data，为分组数组：{ partnerType, partnerTypeName, list:[{ partnerId, partnerName,
 *     partnerLogo, partnerUrl, description }] }），映射为扁平卡片模型 cards + 去重分类 categories。
 *  3. 接口失败兜底空数组，不阻断页面。
 *  4. __seo 复用原页 SEO 文案；fullName 取 siteCheck.data('full-name')，缺省 CJdropshipping。
 *
 * @param {Object} ctx - SSR 直传 Koa ctx 或 ssrDataLoader 统一上下文
 * @param {Function} request - 框架注入的统一请求函数 async (config) => [err, body]
 * @returns {Object} { ossHost, fullName, cards, categories, __seo }
 */
async function fetchData(ctx, request) {
  let ossHost = '';
  const eggCtx = (ctx && ctx.app) ? ctx : (ctx && ctx.ctx);
  if (eggCtx && eggCtx.app && eggCtx.app.config) {
    const viewVar = eggCtx.app.config.view && eggCtx.app.config.view.variable;
    ossHost = (viewVar && (viewVar.OSS_HOST || viewVar.CDN_HOST)) || '';
  }
  // CSR 兜底：window.OSS_HOST → 全局 CDN_HOST（与 OSS_HOST 等值）
  if (!ossHost && typeof window !== 'undefined' && window.OSS_HOST) {
    ossHost = window.OSS_HOST;
  }
  if (!ossHost && typeof CDN_HOST !== 'undefined') {
    ossHost = CDN_HOST;
  }

  // fullName 取站点 full-name（如 CJdropshipping），缺省回退 'CJdropshipping'
  let fullName = '';
  try {
    if (eggCtx && eggCtx.siteCheck && typeof eggCtx.siteCheck.data === 'function') {
      fullName = eggCtx.siteCheck.data('full-name') || '';
    }
  } catch (e) { fullName = ''; }
  if (!fullName && typeof window !== 'undefined' && window.siteCheck && typeof window.siteCheck.data === 'function') {
    try { fullName = window.siteCheck.data('full-name') || ''; } catch (e) { /* ignore */ }
  }
  if (!fullName) { fullName = 'CJdropshipping'; }

  // 260715：siteName 取站点 name（如 CJ），缺省回退 'CJ'，用于固定文案 {{name}} 插值
  let siteName = '';
  try {
    if (eggCtx && eggCtx.siteCheck && typeof eggCtx.siteCheck.data === 'function') {
      siteName = eggCtx.siteCheck.data('name') || '';
    }
  } catch (e) { siteName = ''; }
  if (!siteName && typeof window !== 'undefined' && window.siteCheck && typeof window.siteCheck.data === 'function') {
    try { siteName = window.siteCheck.data('name') || ''; } catch (e) { /* ignore */ }
  }
  if (!siteName) { siteName = 'CJ'; }

  // ===== 平台卡片：接口拉取分组列表，映射为扁平卡片 + 去重分类（失败兜底空数组，不阻断页面） =====
  const cards = [];
  const categories = [];
  if (typeof request === 'function') {
    try {
      const res = await request({
        url: 'cj/partner/getAllTypePlatformList',
        method: 'post',
        data: {},
        headers: { language: 'en' },
      });
      // 后端返回两种结构：{ success:true, data:[...] } 或 { statusCode:200, result:[...] }
      // （与 common/utils.js statusCode200 的判定一致），故 data / result 双兜底取分组数组
      const body = (res && res[1]) || {};
      const groups = body.data || body.result || [];
      groups.forEach(function (group) {
        const catName = (group && group.partnerTypeName) || '';
        if (catName && categories.indexOf(catName) < 0) categories.push(catName);
        const list = (group && group.list) || [];
        list.forEach(function (p) {
          cards.push({
            category: catName,
            name: p.partnerName || '',
            logo: p.partnerLogo || '',
            description: p.description || '',
            link: p.partnerUrl || '',
          });
        });
      });
    } catch (e) {
      // 兜底空数组
    }
  }

  return {
    ossHost: ossHost,
    fullName: fullName,
    siteName: siteName,
    cards: cards,
    categories: categories,
    // 需求来源：docs/合作页、CJ Entity、Ads开户落地页3个/AI提示词.md 第35次提交（需求78）—— 更新合作页 SEO 文案（原文见文档）；
    // 品牌全名走 fullName（缺省 CJdropshipping），文案中「Partner with CJ」的 CJ 走 siteName（缺省 CJ），保持多站点一致。
    __seo: {
      title: `${fullName} Partnership Program | Grow Your Ecommerce Business`,
      description: `Join the ${fullName} Partnership Program to connect with millions of ecommerce sellers. Partner with ${siteName} to grow your platform, app, service, or brand together.`,
      keywords: `${fullName} partnership, ecommerce partnership, dropshipping partner, Shopify partner, sourcing partner, affiliate partnership, ecommerce ecosystem`,
    },
  };
}

module.exports = fetchData;
